import type { PurchaseInvoiceDetailRow, PurchaseInvoiceFieldValue, PurchaseInvoiceLookup } from '../types';
import { detailExportColumns, type DetailExportColumn, type FileDef } from '@/lib/excel/export';
import { roundNumber } from '@/lib/formatUtils';

const text = (value: PurchaseInvoiceFieldValue) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const lookup = (value: PurchaseInvoiceFieldValue): PurchaseInvoiceLookup =>
  value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date) ? value : {};

const amount = (value: PurchaseInvoiceFieldValue) => {
  if (value === null || value === undefined || value === '') return 0;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) throw new Error('Giá trị số trong chi tiết hóa đơn không hợp lệ.');
  return roundNumber(numeric, 6);
};

const unitLabel = (row: PurchaseInvoiceDetailRow) => {
  const product = row.ma_vt_data;
  const selected = row.dvt || product?.dvt_nl || product?.dvt;
  const conversions = product?.quy_doi_dvt_data;
  const conversion = Array.isArray(conversions)
    ? conversions.map(lookup).find(item => item.dvt === selected)
    : undefined;
  return text(
    conversion?.dvt_data?.ten_dvt ||
      (selected === product?.dvt ? product?.dvt_data?.ten_dvt : undefined) ||
      lookup(row.dvt_data).ten_dvt
  );
};

export const hasInvoiceDetail = (row: PurchaseInvoiceDetailRow) => Boolean(row.ma_vt_data?.uuid || row.ma_vt);

export const buildInvoiceDetailFile = (columns: DetailExportColumn[]): FileDef<PurchaseInvoiceDetailRow> => ({
  name: 'ChiTietHoaDonMuaHangTrongNuoc',
  sheet: 'Chi tiết hóa đơn',
  title: 'CHI TIẾT HÓA ĐƠN MUA HÀNG TRONG NƯỚC',
  cols: detailExportColumns<PurchaseInvoiceDetailRow>(columns, {
    ma_vt: row => text(row.ma_vt_data?.ma_vt),
    ten_vt: row => text(row.ma_vt_data?.ten_vt),
    dvt: unitLabel,
    ma_kho: row => text(row.ma_kho_data?.ma_kho),
    ma_vi_tri: row => text(lookup(row.ma_vi_tri_data).ma_vi_tri),
    so_luong: row => amount(row.so_luong),
    gia_nt0: row => amount(row.gia_nt0),
    tien_nt0: row => amount(row.tien_nt0),
    tien_tck_nt: row => amount(row.tien_tck_nt),
    tl_ck: row => amount(row.tl_ck),
    ck_nt: row => amount(row.ck_nt),
    cp_nt: row => amount(row.cp_nt),
    gia_nt: row => amount(row.gia_nt),
    ma_thue: row => text(row.ma_thue_data?.ten_thue),
    thue_nt: row => amount(row.thue_nt),
    tk_cpxt: row => text(row.tk_cpxt_data?.code),
    px_dd: row => Boolean(row.px_dd),
    tk_vt: row => text(row.tk_vt_data?.code || row.ma_vt_data?.tk_khcp_data?.code),
    ma_bp: row => text(row.ma_bp_data?.ma_bp),
    ma_vv: row => text(row.ma_vv_data?.ma_vv),
    ma_hd: row => text(row.ma_hd_data?.ma_hd),
    ma_dtt: row => text(row.ma_dtt_data?.ma_dtt),
    ma_ku: row => text(row.ma_ku_data?.ma_ku),
    ma_phi: row => text(row.ma_phi_data?.ma_phi),
    ma_cp0: row => text(row.ma_cp0_data?.ma_cpkhl || row.ma_cp0_data?.ma_cp),
    id_dh: row => text(lookup(row.id_dh).so_ct),
    line_dh: row => text(lookup(row.line_dh).line),
    so_ct_hd4: row => text(row.so_ct_hd4),
    line_dh4: row => text(row.line_dh4)
  })
});
